// Notifications.js - Performance optimized notification system
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

// Notification Types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Notification Context for global access
const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Individual Notification Component
const NotificationToast = ({ id, type, message, duration, onClose }) => {
  const [removing, setRemoving] = useState(false);

  // Color settings based on notification type
  const settings = {
    [NOTIFICATION_TYPES.SUCCESS]: {
      accentColor: 'bg-green-200',
    },
    [NOTIFICATION_TYPES.WARNING]: {
      accentColor: 'bg-amber-400',
    },
    [NOTIFICATION_TYPES.ERROR]: {
      accentColor: 'bg-red-500',
    },
    [NOTIFICATION_TYPES.INFO]: {
      accentColor: 'bg-blue-400',
    }
  };

  // Auto-remove after duration
  useEffect(() => {
    if (duration !== Infinity) {
      const timer = setTimeout(() => {
        setRemoving(true);
        setTimeout(() => onClose(id), 300); // Allow time for fade-out animation
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);

  return (
    <div 
      className={`max-w-md w-full bg-black text-white shadow-lg rounded-md overflow-hidden flex mb-4
                 transition-all duration-300 ease-in-out transform
                 ${removing ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}`}
    >
      {/* Accent color line */}
      <div className={`w-1 ${settings[type].accentColor}`}></div>
      
      <div className="flex-1 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-white">{message}</p>
          <button 
            onClick={() => {
              setRemoving(true);
              setTimeout(() => onClose(id), 300);
            }} 
            className="ml-4 bg-transparent rounded-md inline-flex text-gray-400 hover:text-gray-300 focus:outline-none"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Add a new notification - MEMOIZED
const addNotification = useCallback((type, message, duration = 5000) => {
  const id = Math.random().toString(36).substring(2, 9);
  
  setNotifications(prev => {
    // Check if identical notification already exists
    const isDuplicate = prev.some(
      notification => notification.type === type && notification.message === message
    );
    
    // If duplicate exists, don't add new notification
    if (isDuplicate) {
      return prev;
    }
    
    // Otherwise, add new notification
    return [...prev, { id, type, message, duration }];
  });
  
  return id;
}, []);

  // Remove a notification by ID - MEMOIZED
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Helper functions for different notification types - ALL MEMOIZED
  const success = useCallback((message, duration) => 
    addNotification(NOTIFICATION_TYPES.SUCCESS, message, duration), [addNotification]);
  
  const error = useCallback((message, duration) => 
    addNotification(NOTIFICATION_TYPES.ERROR, message, duration), [addNotification]);
  
  const warning = useCallback((message, duration) => 
    addNotification(NOTIFICATION_TYPES.WARNING, message, duration), [addNotification]);
  
  const info = useCallback((message, duration) => 
    addNotification(NOTIFICATION_TYPES.INFO, message, duration), [addNotification]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

// Context value - callbacks are already memoized for performance
const contextValue = {
  notifications, 
  addNotification, 
  removeNotification, 
  success, 
  error, 
  warning, 
  info,
  clearAll
};

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <div className="fixed top-0 right-4 z-[9999] pt-4 space-y-4 flex flex-col items-end">
        {notifications.map(notification => (
          <NotificationToast
            key={notification.id}
            id={notification.id}
            type={notification.type}
            message={notification.message}
            duration={notification.duration}
            onClose={removeNotification}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

// Create specialized notification functions for authentication scenarios - MEMOIZED
export const createAuthNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    loginSuccess: () => success(
      "You have been successfully logged in. Redirecting..."
    ),
    
    signupSuccess: () => success(
      "Your account has been successfully created. Redirecting..."
    ),
    
    passwordResetSuccess: () => success(
      "Your password has been successfully reset."
    ),
    
    verificationSuccess: () => success(
      "Your email has been successfully verified."
    ),

    // Warning notifications
    emailNotFound: () => warning(
      "Email address not found. Please check and try again."
    ),
    
    invalidCredentials: () => warning(
      "The email or password you entered doesn't match our records. Please check and try again."
    ),

    passwordMismatch: () => warning(
      "The passwords you entered don't match. Please ensure both passwords are the same."
    ),
    
    otpVerificationFailed: () => warning(
      "The verification code you entered is incorrect or has expired. Please try again."
    ),
    
    rateLimitExceeded: (retryTime) => warning(
      `For security reasons, access has been temporarily limited. Please try again after ${retryTime}.`
    ),
    
    sessionExpired: () => warning(
      "Your authentication session has expired. Please try signing in again."
    ),
    
    emailAlreadyRegistered: () => warning(
      "This email is already registered. Please sign in or use a different email address."
    ),
    
    // Error notifications - only for system errors, not user errors
    networkError: () => error(
      "We're having trouble connecting to our servers. Please check your internet connection and try again."
    ),
    
    serverError: (message) => error(
      message || "We're experiencing technical difficulties. Please try again later. If the problem persists, contact support."
    ),
    
    // Info notifications
    verificationEmailSent: () => info(
      "A verification code has been sent to your email address. Please check your inbox."
    ),
    
    // Additional notifications for authentication flow
    oauthCancelled: () => warning(
      "Authentication process was interrupted. Please try signing in again."
    ),
    
    googleSigninFailed: () => warning(
      "We couldn't complete your Google sign-in. Please try again or use another sign-in method."
    ),
    
    googleInfoRetrievalFailed: () => warning(
      "We couldn't retrieve your Google account information. Please ensure you've granted the necessary permissions."
    ),
    
    accountCreationFailed: () => error(
      "We couldn't create your account. Please try again later or contact support."
    ),
    
    accountSetupFailed: () => error(
      "We couldn't set up your account completely. Please try again or contact support."
    ),
    
    autoLoginFailed: () => warning(
      "Your account was created successfully, but we couldn't sign you in automatically. Please try signing in manually."
    ),
    
    accountUpdateFailed: () => error(
      "We couldn't update your account settings. Please try again later."
    ),
    
    resendCodeConfirmation: () => info(
      "A new verification code has been sent to your email."
    ),
    
    wrongProviderWarning: (provider) => warning(
      `This account was created using ${provider}. Please use that method to sign in.`
    ),
    
    passwordTooWeak: () => warning(
      "Your password doesn't meet the minimum security requirements. Please use a stronger password."
    ),
    
    registrationValidationError: () => warning(
      "Please check the form for errors and try again."
    ),
    
    registrationInterrupted: () => warning(
      "Your registration was interrupted. Please try again."
    ),
    
    otpLoginModeToggled: (isOtpMode) => info(
      isOtpMode ? "Sign in with one-time code selected." : "Sign in with password selected."
    ),
    
    accountVerificationRequired: () => info(
      "Please verify your email address to complete your registration."
    )
  };
};
// Update in your Notifications.js createAIChatNotifications function

export const createAIChatNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return {
    // Warning notifications
    insufficientCredits: () => warning(
      "Insufficient credits. Please add credits to continue.",
      8000 // Longer duration for important message
    ),
    
    modelNotAvailable: () => warning(
      "Selected AI model is not available. Please choose a different model."
    ),
    
    rateLimitExceeded: () => warning(
      "Rate limit exceeded. Please wait 60 seconds before sending another message."
    ),
    
    invalidParameters: (errors) => warning(
      `Invalid parameters: ${errors.join(', ')}. Please check your settings.`
    ),
    
    contextTooLong: () => warning(
      "Your message is too long. Please shorten it and try again."
    ),
    
    // Error notifications
    chatSendFailed: (message) => error(
      message || "Failed to send message. Please try again."
    ),
    
    modelsLoadFailed: () => error(
      "Failed to load AI models. Please refresh the page."
    ),
    
    aiServiceError: () => error(
      "AI service is temporarily unavailable. Please try again later."
    ),
    
    networkError: () => error(
      "Network connection error. Please check your internet and try again."
    )
  };
};
// Create specialized notification functions for firewall operations - MEMOIZED
export const createFirewallNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    firewallCreated: (name) => success(
      `Firewall "${name}" has been successfully created.`
    ),
    
    firewallDeleted: () => success(
      "Firewall has been successfully deleted."
    ),
    
    firewallRuleAdded: () => success(
      "Firewall rule has been successfully added."
    ),
    
    firewallRuleDeleted: () => success(
      "Firewall rule has been successfully deleted."
    ),
    
    firewallAttached: (vmCount) => success(
      `Firewall has been successfully attached to ${vmCount} ${vmCount === 1 ? 'VM' : 'VMs'}.`
    ),
    
    firewallDetached: () => success(
      "Firewall has been successfully detached from VM."
    ),
    
    // Warning notifications
    invalidRuleParameters: (message) => warning(
      message || "Invalid rule parameters. Please check your inputs and try again."
    ),
    
    noVmsSelected: () => warning(
      "No VMs selected. Please select at least one VM to attach the firewall to."
    ),
    
    vmNotCompatible: () => warning(
      "One or more selected VMs are not compatible with this firewall. VMs must be in the same region as the firewall."
    ),
    
    regionNotAvailable: () => warning(
      "The selected region is not available. Please select a different region."
    ),
    
    // Error notifications
    firewallCreateFailed: (message) => error(
      message || "Failed to create firewall. Please try again later."
    ),
    
    firewallDeleteFailed: (message) => error(
      message || "Failed to delete firewall. Please try again later."
    ),
    
    firewallRuleAddFailed: (message) => error(
      message || "Failed to add firewall rule. Please try again later."
    ),
    
    firewallRuleDeleteFailed: (message) => error(
      message || "Failed to delete firewall rule. Please try again later."
    ),

    firewallAttachFailed: (message) => error(
      message || "Failed to attach firewall to VMs. Please try again later."
    ),
    
    firewallDetachFailed: (message) => error(
      message || "Failed to detach firewall from VM. Please try again later."
    ),
    
    firewallListFailed: () => error(
      "Failed to retrieve firewalls. Please try refreshing the page."
    ),
    
    firewallDetailsFailed: () => error(
      "Failed to retrieve firewall details. Please try refreshing the page."
    ),
 
    // Info notifications
    firewallLoading: () => info(
      "Loading firewall information. This may take a few moments..."
    ),
    
    firewallAttaching: () => info(
      "Attaching firewall to VMs. This may take a few moments..."
    ),
    
    firewallDetaching: () => info(
      "Detaching firewall from VM. This may take a few moments..."
    ),
    
    firewallCreating: () => info(
      "Creating firewall. This may take a few moments..."
    ),
    
    ruleValidating: () => info(
      "Validating rule parameters..."
    ),
    
    alreadyAttached: (count) => info(
      `${count} ${count === 1 ? 'VM is' : 'VMs are'} already attached to this firewall.`
    )
  };
};

// Create specialized notification functions for volume operations - MEMOIZED
export const createVolumeNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    volumeCreated: (name) => success(
      `Volume "${name}" has been successfully created.`
    ),
    
    volumeDeleted: (name) => success(
      `Volume "${name}" has been successfully deleted.`
    ),
    
    volumeAttached: () => success(
      "Volume has been successfully attached to the VM."
    ),
    
    volumeDetached: (name) => success(
      `Volume "${name}" has been successfully detached from the VM.`
    ),
    
    // Warning notifications
    volumeNameExists: () => warning(
      "A volume with this name already exists. Please use a different name."
    ),
    
    invalidVolumeParameters: (message) => warning(
      message || "Invalid volume parameters. Please check your inputs and try again."
    ),
    
    invalidVolumeSize: (min, max) => warning(
      `Volume size must be between ${min}GB and ${max}GB.`
    ),
    
    bootableVolumeMinSize: (minSize) => warning(
      `Bootable volumes require at least ${minSize}GB of storage.`
    ),
    
    vmNotActive: () => warning(
      "The VM must be in ACTIVE state for this operation. Please start the VM first."
    ),
    
    noVmsAvailable: () => warning(
      "No VMs available for attachment. Please create a VM in this region first."
    ),
    
    vmRegionMismatch: () => warning(
      "VM and volume must be in the same region for attachment."
    ),
    
    noActiveVms: () => warning(
      "No active VMs available. You must start a VM before attaching volumes."
    ),
    
    // Error notifications
    volumeCreateFailed: (message) => error(
      message || "Failed to create volume. Please try again later."
    ),
    
    volumeDeleteFailed: (message) => error(
      message || "Failed to delete volume. Please try again later."
    ),
    
    volumeAttachFailed: (message) => error(
      message || "Failed to attach volume to VM. Please try again later."
    ),
    
    volumeDetachFailed: (message) => error(
      message || "Failed to detach volume from VM. Please try again later."
    ),
    
    volumeListFailed: () => error(
      "Failed to retrieve volumes. Please try refreshing the page."
    ),
    
    volumeDetailsFailed: () => error(
      "Failed to retrieve volume details. Please try refreshing the page."
    ),
    
    volumeNotFound: () => error(
      "Volume not found. It may have been deleted or never existed."
    ),
    
    // Info notifications
    volumeCreating: () => info(
      "Creating volume. This may take a few moments..."
    ),
    
    volumeAttaching: () => info(
      "Attaching volume to VM. This may take a few moments..."
    ),
    
    volumeDetaching: () => info(
      "Detaching volume from VM. This may take a few moments..."
    ),
    
    volumeDeleting: () => info(
      "Deleting volume. This may take a few moments..."
    )
  };
};

// Create specialized notification functions for VM deployment stages - MEMOIZED
export const createDeploymentNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Deployment Stage Notifications - Success
    deploymentInitiated: () => info(
      "VM deployment has been initiated. This process may take a few minutes..."
    ),
    
    deploymentValidated: () => info(
      "VM configuration validated. Creating VM..."
    ),
    
    deploymentCompleted: (vmName, vmId) => success(
      `VM "${vmName}" (${vmId}) has been successfully deployed.`,
      8000 // Longer duration for important message
    ),
    
    deploymentSecurityRulesApplied: () => success(
      "Security rules have been successfully applied to your VM."
    ),
    
    vmDeleted: (vmName) => success(
      `VM "${vmName}" has been successfully deleted.`
    ),
    
    vmStarted: (vmName) => success(
      `VM "${vmName}" has been successfully started.`
    ),
    
    vmStopped: (vmName) => success(
      `VM "${vmName}" has been successfully stopped.`
    ),
    
    vmRebooted: (vmName) => success(
      `VM "${vmName}" has been successfully rebooted.`
    ),
    
    vmHibernated: (vmName) => success(
      `VM "${vmName}" has been hibernated successfully. You will be charged at a reduced rate while hibernated.`
    ),
    
    vmRestored: (vmName) => success(
      `VM "${vmName}" has been successfully restored from hibernation.`
    ),
    
    vmResized: (vmName, flavor) => success(
      `VM "${vmName}" has been successfully resized to ${flavor}.`
    ),
    
    // Deployment Stage Notifications - In Progress
    checkingDeploymentStatus: () => info(
      "Checking VM deployment status..."
    ),
    
    configureNetworking: () => info(
      "Configuring network settings for your VM..."
    ),
    
    applyingSecurityRules: () => info(
      "Applying security rules to your VM..."
    ),
    
    provisioningStorage: () => info(
      "Provisioning storage for your VM..."
    ),
    
    allocatingResources: () => info(
      "Allocating GPU and compute resources..."
    ),
    
    installingDependencies: () => info(
      "Installing system dependencies and drivers..."
    ),
    
    finalizingSetup: () => info(
      "Finalizing VM setup. Almost ready..."
    ),

    vmStarting: (vmName) => info(
      `Starting VM "${vmName}". This may take a few moments...`
    ),
    
    vmStopping: (vmName) => info(
      `Stopping VM "${vmName}". This may take a few moments...`
    ),
    
    vmRebooting: (vmName) => info(
      `Rebooting VM "${vmName}". This may take a few moments...`
    ),
    
    vmHibernating: (vmName) => info(
      `Hibernating VM "${vmName}". This may take a few moments...`
    ),
    
    vmRestoring: (vmName) => info(
      `Restoring VM "${vmName}" from hibernation. This may take a few moments...`
    ),
    
    vmResizing: (vmName) => info(
      `Resizing VM "${vmName}". This may take several minutes...`,
      8000 // Longer duration for longer processes
    ),
    
    vmDeleting: (vmName) => info(
      `Deleting VM "${vmName}". This may take a few moments...`
    ),
    
    // Deployment Stage Notifications - Warnings
    deploymentValidationWarning: (issues) => warning(
      `Validation warning: ${issues}. Deployment will continue but may have issues.`
    ),
    
    insufficientCredits: (requiredAmount, currency) => warning(
      `Insufficient credits to deploy this VM. Required: ${currency}${requiredAmount}. Please add credits to your account.`,
      10000 // Longer duration for important credit warnings
    ),
    
    gpuNotAvailable: (gpuType, region) => warning(
      `${gpuType} GPUs are currently unavailable in ${region}. Please select a different GPU type or region.`
    ),
    
    regionCapacityExceeded: (region) => warning(
      `The ${region} region is currently at capacity. Please select a different region or try again later.`
    ),
    
    sshKeyRequired: () => warning(
      "An SSH key is required for VM deployment. Please add or select an SSH key."
    ),
    
    adminPasswordRequired: () => warning(
      "An administrator password is required for this VM. Please provide a password."
    ),
    
    osImageRequired: () => warning(
      "An operating system image is required. Please select an OS image."
    ),
    
    jupyterPasswordWeak: () => warning(
      "The Jupyter password is too weak. Please use a stronger password with at least 8 characters."
    ),
    
    securityRulePartialFailure: (rulesApplied, totalRules) => warning(
      `Only ${rulesApplied} of ${totalRules} security rules were successfully applied. You can add remaining rules later.`
    ),
    
    // Deployment Stage Notifications - Errors
    deploymentFailed: (reason) => error(
      `VM deployment failed: ${reason || 'Unknown error'}. Please try again or contact support.`
    ),
    
    vmDeleteFailed: (vmName, reason) => error(
      `Failed to delete VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmStartFailed: (vmName, reason) => error(
      `Failed to start VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmStopFailed: (vmName, reason) => error(
      `Failed to stop VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmRebootFailed: (vmName, reason) => error(
      `Failed to reboot VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmHibernateFailed: (vmName, reason) => error(
      `Failed to hibernate VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmRestoreFailed: (vmName, reason) => error(
      `Failed to restore VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmResizeFailed: (vmName, reason) => error(
      `Failed to resize VM "${vmName}": ${reason || 'Unknown error'}. Please try again later.`
    ),
    
    vmStatusCheckFailed: () => error(
      "Failed to check VM status. Please try refreshing the page."
    ),
    
    securityRuleApplicationFailed: () => error(
      "Failed to apply security rules to VM. You can try adding them later from the VM details page."
    ),
    
    cloudInitScriptInvalid: () => error(
      "The provided cloud-init script is invalid. Please check your script and try again."
    ),
    
    callbackRegistrationFailed: () => info(
      "VM status update registration failed. Status updates may be delayed."
    ),
    
    deploymentTimeout: () => warning(
      "VM deployment is taking longer than expected. The process will continue in the background, please check the dashboard for status.",
      10000 // Longer duration
    ),
    
    flavorIncompatible: () => warning(
      "The selected VM configuration is not compatible with this operation. Please select a different configuration."
    )
  };
};

// Create specialized notification functions for image operations - MEMOIZED
export const createImageNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    imageCreated: (name) => success(
      `Image "${name}" has been successfully created.`
    ),
    
    imageDeleted: (name) => success(
      `Image "${name}" has been successfully deleted.`
    ),
    
    // Warning notifications
    imageNameExists: () => warning(
      "An image with this name already exists. Please use a different name."
    ),
    
    invalidImageParameters: (message) => warning(
      message || "Invalid image parameters. Please check your inputs and try again."
    ),
    
    imageSizeTooLarge: () => warning(
      "The selected snapshot is too large for image creation. Please select a smaller snapshot."
    ),
    
    noSnapshotsAvailable: () => warning(
      "No snapshots are available to create an image. Please create a snapshot first."
    ),
    
    snapshotNotFound: () => warning(
      "The selected snapshot was not found. It may have been deleted."
    ),
    
    imageLimitReached: (limit) => warning(
      `You have reached the maximum limit of ${limit} custom images. Please delete unused images first.`
    ),
    
    imageNameTooLong: (maxLength) => warning(
      `Image name must be ${maxLength} characters or less.`
    ),
    
    // Error notifications
    imageCreateFailed: (message) => error(
      message || "Failed to create image. Please try again later."
    ),
    
    imageDeleteFailed: (message) => error(
      message || "Failed to delete image. Please try again later."
    ),
    
    imageListFailed: () => error(
      "Failed to retrieve images. Please try refreshing the page."
    ),
    
    imageDetailsFailed: () => error(
      "Failed to retrieve image details. Please try refreshing the page."
    ),
    
    imageNotFound: () => error(
      "Image not found. It may have been deleted or never existed."
    ),
    
    // Info notifications
    imageCreating: () => info(
      "Creating image from snapshot. This may take a few moments..."
    ),
    
    imageDeleting: () => info(
      "Deleting image. This may take a few moments..."
    ),
    
    imageFetching: () => info(
      "Fetching images. This may take a few moments..."
    )
  };
};

// Create specialized notification functions for snapshot operations - MEMOIZED
export const createSnapshotNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    snapshotCreated: (name) => success(
      `Snapshot "${name}" has been successfully created.`
    ),
    
    snapshotDeleted: (name) => success(
      `Snapshot "${name}" has been successfully deleted.`
    ),
    
    snapshotRestored: () => success(
      "Snapshot has been successfully restored to a new VM."
    ),
    
    // Warning notifications
    snapshotNameExists: () => warning(
      "A snapshot with this name already exists. Please use a different name."
    ),
    
    invalidSnapshotParameters: (message) => warning(
      message || "Invalid snapshot parameters. Please check your inputs and try again."
    ),
    
    snapshotNameTooLong: (maxLength) => warning(
      `Snapshot name must be ${maxLength} characters or less.`
    ),
    
    vmNotActive: () => warning(
      "The VM must be in ACTIVE state for snapshot operations. Please start the VM first."
    ),
    
    noVmsAvailable: () => warning(
      "No VMs available for snapshot creation. Please start a VM in this region first."
    ),
    
    vmNotCompatible: () => warning(
      "The selected VM is not compatible with this operation. Please select another VM."
    ),
    
    noActiveVms: () => warning(
      "No active VMs available. You must have at least one active VM to create a snapshot."
    ),
    
    snapshotInUse: () => warning(
      "This snapshot is currently in use. Please try again later."
    ),
    
    vmUnavailable: () => warning(
      "The selected VM is currently unavailable. Please try again later."
    ),
    
    // Error notifications
    snapshotCreateFailed: (message) => error(
      message || "Failed to create snapshot. Please try again later."
    ),
    
    snapshotDeleteFailed: (message) => error(
      message || "Failed to delete snapshot. Please try again later."
    ),
    
    snapshotRestoreFailed: (message) => error(
      message || "Failed to restore snapshot. Please try again later."
    ),
    
    snapshotListFailed: () => error(
      "Failed to retrieve snapshots. Please try refreshing the page."
    ),
    
    snapshotDetailsFailed: () => error(
      "Failed to retrieve snapshot details. Please try refreshing the page."
    ),
    
    snapshotNotFound: () => error(
      "Snapshot not found. It may have been deleted or never existed."
    ),
    
    // Info notifications
    snapshotCreating: () => info(
      "Creating snapshot. This may take a few moments..."
    ),
    
    snapshotDeleting: () => info(
      "Deleting snapshot. This may take a few moments..."
    ),
    
    snapshotRestoring: () => info(
      "Restoring snapshot to a new VM. This may take a few moments..."
    ),
    
    snapshotsFetching: () => info(
      "Fetching snapshots. This may take a few moments..."
    ),
    
    vmsFetching: () => info(
      "Fetching available VMs. This may take a few moments..."
    )
  };
};

// Create specialized notification functions for SSH key operations - MEMOIZED
export const createSSHKeyNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    sshKeyCreated: (name) => success(
      `SSH key "${name}" has been successfully created.`
    ),
    
    sshKeyImported: (name) => success(
      `SSH key "${name}" has been successfully imported.`
    ),
    
    sshKeyDeleted: (name) => success(
      `SSH key "${name}" has been successfully deleted.`
    ),
    
    sshKeyUpdated: (name) => success(
      `SSH key "${name}" has been successfully updated.`
    ),
    
    sshKeyDownloaded: (name) => success(
      `Private key "${name}" has been downloaded.`
    ),
    
    sshKeyCopied: () => success(
      "SSH key has been copied to clipboard."
    ),
    
    // Warning notifications
    sshKeyNameExists: () => warning(
      "An SSH key with this name already exists. Please use a different name."
    ),
    
    invalidSSHKeyName: (message) => warning(
      message || "Invalid SSH key name. Key names must be alphanumeric with optional hyphens and underscores."
    ),
    
    invalidSSHKeyFormat: () => warning(
      "Invalid SSH public key format. Key should start with ssh-rsa, ssh-ed25519, etc."
    ),
    
    sshKeyNameTooLong: (maxLength) => warning(
      `SSH key name must be ${maxLength} characters or less.`
    ),
    
    keyLimitReached: (limit) => warning(
      `You have reached the maximum limit of ${limit} SSH keys. Please delete unused keys first.`
    ),
    
    // Error notifications
    sshKeyCreateFailed: (message) => error(
      message || "Failed to create SSH key. Please try again later."
    ),
    
    sshKeyImportFailed: (message) => error(
      message || "Failed to import SSH key. Please check the key format and try again."
    ),
    
    sshKeyDeleteFailed: (message) => error(
      message || "Failed to delete SSH key. Please try again later."
    ),
    
    sshKeyUpdateFailed: (message) => error(
      message || "Failed to update SSH key. Please try again later."
    ),
    
    sshKeyListFailed: () => error(
      "Failed to retrieve SSH keys. Please try refreshing the page."
    ),
    
    sshKeyNotFound: () => error(
      "SSH key not found. It may have been deleted or never existed."
    ),
    
    // Info notifications
    sshKeyCreating: () => info(
      "Creating SSH key. This may take a few moments..."
    ),
    
    sshKeyImporting: () => info(
      "Importing SSH key. This may take a few moments..."
    ),
    
    sshKeyDeleting: () => info(
      "Deleting SSH key. This may take a few moments..."
    ),
    
    sshKeyUpdating: () => info(
      "Updating SSH key. This may take a few moments..."
    ),
    
    sshKeyFetching: () => info(
      "Fetching SSH keys. This may take a few moments..."
    ),
    
    sshKeySelected: (name) => info(
      `SSH key "${name}" selected.`
    ),
    
    privateKeyWarning: () => warning(
      "IMPORTANT: This is your only chance to download this private key. Please save it securely."
    )
  };
};

// Create specialized notification functions for profile/account operations - MEMOIZED
export const createProfileNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    profileUpdated: () => success(
      "Your profile has been successfully updated."
    ),
    
    passwordChanged: () => success(
      "Your password has been successfully changed."
    ),
    
    emailVerified: () => success(
      "Your email has been successfully verified."
    ),
    
    profilePictureUploaded: () => success(
      "Your profile picture has been successfully uploaded."
    ),
    
    profilePictureRemoved: () => success(
      "Your profile picture has been successfully removed."
    ),
    
    settingsSaved: () => success(
      "Your account settings have been successfully saved."
    ),
    
    preferencesSaved: () => success(
      "Your preferences have been successfully saved."
    ),
    
    paymentMethodAdded: () => success(
      "Your payment method has been successfully added."
    ),
    
    paymentMethodRemoved: () => success(
      "Your payment method has been successfully removed."
    ),
    
    paymentMethodUpdated: () => success(
      "Your payment method has been successfully updated."
    ),
    
    apiKeyGenerated: () => success(
      "Your API key has been successfully generated."
    ),
    
    apiKeyDeleted: () => success(
      "Your API key has been successfully deleted."
    ),
    
    twoFactorEnabled: () => success(
      "Two-factor authentication has been successfully enabled."
    ),
    
    twoFactorDisabled: () => success(
      "Two-factor authentication has been successfully disabled."
    ),
    
    notificationSettingsUpdated: () => success(
      "Your notification settings have been successfully updated."
    ),
    
    // Warning notifications
    passwordMismatch: () => warning(
      "The passwords you entered don't match. Please ensure both passwords are the same."
    ),
    
    oldPasswordIncorrect: () => warning(
      "Your current password is incorrect. Please check and try again."
    ),
    
    passwordRequirements: () => warning(
      "Your password doesn't meet the security requirements. Please use a stronger password."
    ),
    
    emailVerificationRequired: () => warning(
      "Email verification is required to complete this action. Please check your inbox."
    ),
    
    invalidApiKeyFormat: () => warning(
      "Invalid API key format. Please check and try again."
    ),
    
    twoFactorCodeInvalid: () => warning(
      "The two-factor authentication code you entered is invalid. Please try again."
    ),
    
    profilePictureSizeExceeded: (maxSize) => warning(
      `Profile picture size exceeds the maximum limit of ${maxSize}. Please select a smaller image.`
    ),
    
    profilePictureFormatInvalid: () => warning(
      "Invalid profile picture format. Please use JPG, PNG, or GIF."
    ),
    
    // Error notifications
    profileUpdateFailed: (message) => error(
      message || "Failed to update your profile. Please try again later."
    ),
    
    profileFetchFailed: () => error(
      "Failed to fetch your profile data. Please refresh the page."
    ),
    
    passwordChangeFailed: (message) => error(
      message || "Failed to change your password. Please try again later."
    ),
    
    emailVerificationFailed: () => error(
      "Failed to verify your email. Please try again or contact support."
    ),
    
    profilePictureUploadFailed: () => error(
      "Failed to upload profile picture. Please try again later."
    ),
    
    settingsSaveFailed: () => error(
      "Failed to save account settings. Please try again later."
    ),
    
    paymentMethodAddFailed: () => error(
      "Failed to add payment method. Please check your payment details and try again."
    ),
    
    paymentMethodRemoveFailed: () => error(
      "Failed to remove payment method. Please try again later."
    ),
    
    paymentMethodUpdateFailed: () => error(
      "Failed to update payment method. Please try again later."
    ),
    
    billingHistoryFetchFailed: () => error(
      "Failed to fetch billing history. Please try again later."
    ),
    
    apiKeyGenerationFailed: () => error(
      "Failed to generate API key. Please try again later."
    ),
    
    apiKeyDeletionFailed: () => error(
      "Failed to delete API key. Please try again later."
    ),
    
    twoFactorSetupFailed: () => error(
      "Failed to set up two-factor authentication. Please try again later."
    ),
    
    twoFactorDisableFailed: () => error(
      "Failed to disable two-factor authentication. Please try again later."
    ),
    
    notificationSettingsUpdateFailed: () => error(
      "Failed to update notification settings. Please try again later."
    ),
    
    // Info notifications
    profileUpdating: () => info(
      "Updating your profile..."
    ),
    
    passwordChanging: () => info(
      "Changing your password..."
    ),
    
    emailVerifying: () => info(
      "Verifying your email..."
    ),
    
    profilePictureUploading: () => info(
      "Uploading your profile picture..."
    ),
    
    settingsSaving: () => info(
      "Saving your account settings..."
    ),
    
    paymentMethodAdding: () => info(
      "Adding your payment method..."
    ),
    
    paymentMethodRemoving: () => info(
      "Removing your payment method..."
    ),
    
    paymentMethodUpdating: () => info(
      "Updating your payment method..."
    ),
    
    apiKeyGenerating: () => info(
      "Generating your API key..."
    ),
    
    twoFactorEnabling: () => info(
      "Enabling two-factor authentication..."
    ),
    
    twoFactorDisabling: () => info(
      "Disabling two-factor authentication..."
    ),
    
    verificationEmailSent: () => info(
      "A verification email has been sent. Please check your inbox."
    ),
    
    backupCodesSaved: () => info(
      "Make sure to save your backup codes in a secure location. You'll need them if you lose access to your authentication device."
    ),
    
    emailChangeRequested: () => info(
      "A confirmation email has been sent to your new email address. Please verify to complete the change."
    )
  };
};

// Create specialized notification functions for payment operations - MEMOIZED
export const createPaymentNotifications = (notificationFunctions) => {
  const { success, error, warning, info } = notificationFunctions;
  
  return{
    // Success notifications
    paymentSuccessful: (amount, currency) => success(
      `Payment of ${currency}${amount} completed successfully.`
    ),
    
    creditsPurchased: (amount, currency) => success(
      `${amount} credits have been added to your account.`
    ),
    
    paymentInitiated: () => info(
      "Payment initiated. Please complete the payment process."
    ),
    
    // Processing notifications
    paymentProcessing: () => info(
      "Processing your payment. This may take a moment..."
    ),
    
    // Error notifications
    paymentFailed: (message) => error(
      message || "Payment failed. Please try again or use a different payment method."
    ),
    
    paymentVerificationFailed: () => error(
      "Payment verification failed. If your account was charged, please contact support."
    ),
    
    insufficientBalance: () => warning(
      "You don't have enough credits for this operation. Please add more credits to your account."
    ),
    
    paymentInitializationFailed: (message) => error(
      message || "Failed to initialize payment. Please try again later."
    ),
    
    unsupportedCurrency: (supportedCurrencies) => warning(
      `The selected currency is not supported. Please use one of these currencies: ${supportedCurrencies.join(', ')}.`
    ),
    
    minimumAmountWarning: (minAmount, currency) => warning(
      `The minimum payment amount is ${currency}${minAmount}.`
    ),
    
    gatewayError: () => error(
      "There was an error with the payment gateway. Please try again later."
    ),
    
    incompleteProfile: () => warning(
      "Please complete your profile before making a payment."
    ),
    
    // Info notifications
    orderCreated: () => info(
      "Payment order created. Please proceed to payment."
    ),
    
    paymentCancelled: () => info(
      "Payment was cancelled. No charges were made."
    ),
    
    transactionHistoryUpdated: () => info(
      "Transaction history has been updated."
    ),
    
    razorpayLoading: () => info(
      "Loading payment processor..."
    )
  };
};

// Full notification components from your original file
export const PaymentOperationNotification = ({ isVisible, operation, amount, currency, onClose }) => {
  if (!isVisible) return null;
  
  let title = "";
  let message = "";
  let isSuccess = false;
  let isWarning = false;
  let isError = false;
  
  switch (operation) {
    case 'success':
      title = "Payment Successful";
      message = `Your payment of ${currency}${amount} was processed successfully.`;
      isSuccess = true;
      break;
      
    case 'failed':
      title = "Payment Failed";
      message = "We couldn't process your payment. Please try again or use a different payment method.";
      isError = true;
      break;
      
    case 'verification_failed':
      title = "Verification Failed";
      message = "We couldn't verify your payment. If your account was charged, please contact support.";
      isWarning = true;
      break;
      
    case 'credits_added':
      title = "Credits Added";
      message = `${amount} credits have been added to your account.`;
      isSuccess = true;
      break;
      
    default:
      title = "Payment Status";
      message = "Payment status updated.";
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content payment-operation-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          {isSuccess && <CheckCircle size={20} className="text-green-500" />}
          {isWarning && <AlertTriangle size={20} className="text-yellow-500" />}
          {isError && <XCircle size={20} className="text-red-500" />}
          {!isSuccess && !isWarning && !isError && <Info size={20} className="text-blue-500" />}
          <p>{title}</p>
        </div>
        
        <p className="mt-2">{message}</p>
        
        <div className="notification-actions mt-4">
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
 
export const ImageOperationNotification = ({ isVisible, operation, imageName, snapshotName, onClose }) => {
  if (!isVisible) return null;
  
  let title = "";
  let details = [];
  
  switch (operation) {
    case 'created':
      title = "Image Created Successfully";
      details = [
        { label: "Name", value: imageName },
        { label: "From Snapshot", value: snapshotName }
      ];
      break;
    case 'deleted':
      title = "Image Deleted Successfully";
      details = [
        { label: "Image", value: imageName }
      ];
      break;
    default:
      title = "Operation Completed";
      if (imageName) details.push({ label: "Image", value: imageName });
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content image-operation-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <CheckCircle size={20} className="text-green-500" />
          <p>{title}</p>
        </div>
        <div className="image-details">
          {details.map((detail, index) => (
            <p key={index}><strong>{detail.label}:</strong> {detail.value}</p>
          ))}
        </div>
        
        <div className="notification-actions">
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
 
export const InsufficientCreditsNotification = ({ isVisible, requiredAmount, gpuType, billingPlan }) => {
  if (!isVisible) return null;
  
  let message = `You need at least ${requiredAmount} credits to deploy ${gpuType} server.`;
  
  if (billingPlan === 'daily') {
    message = `You need at least ${requiredAmount} credits to deploy ${gpuType} 24-hour pack.`;
  } else if (billingPlan === 'weekly') {
    message = `You need at least ${requiredAmount} credits to deploy ${gpuType} 7-day pack.`;
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content insufficient-credits-notification-content">
        <div className="notification-progress-bar"></div>
        <p>Insufficient Credits</p>
        <p>{message}</p>
        <p>Please add credits to your account to continue.</p>
      </div>
    </div>
  );
};
 
export const ActiveServersWithdrawalNotification = ({ isVisible, serverCount, onClose, onManageServers }) => {
  if (!isVisible) return null;
 
  return (
    <div className="notification-container">
      <div className="notification-content active-servers-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <AlertTriangle size={20} />
          <p>Active Servers Detected</p>
        </div>
        <p>You have {serverCount} active server{serverCount !== 1 ? 's' : ''} in your account.</p>
        <p>Please delete all active servers before requesting a withdrawal.</p>
        
        <div className="notification-actions">
          <button className="notification-button secondary" onClick={onClose}>
            Close
          </button>
          <button className="notification-button primary" onClick={onManageServers}>
            Manage Servers
          </button>
        </div>
      </div>
    </div>
  );
};
 
export const FirewallRuleNotification = ({ isVisible, rule, onClose }) => {
  if (!isVisible) return null;
  
  return (
    <div className="notification-container">
      <div className="notification-content firewall-rule-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <CheckCircle size={20} className="text-green-500" />
          <p>Rule Added Successfully</p>
        </div>
        <div className="rule-details">
          <p><strong>Direction:</strong> {rule.direction}</p>
          <p><strong>Protocol:</strong> {rule.protocol.toUpperCase()}</p>
          <p><strong>Port Range:</strong> {rule.port_range_min && rule.port_range_max ? 
            `${rule.port_range_min} - ${rule.port_range_max}` : 'All Ports'}</p>
          <p><strong>Remote IP:</strong> {rule.remote_ip_prefix}</p>
        </div>
        
        <div className="notification-actions">
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const VMDeploymentStatusNotification = ({ isVisible, status, vmName, vmId, deploymentPhase, progress, onClose, onViewDetails }) => {
  if (!isVisible) return null;
  
  let title = "";
  let message = "";
  let showProgress = false;
  let showViewButton = false;
  let isSuccess = false;
  let isWarning = false;
  let isError = false;
  
  switch (status) {
    case 'initiated':
      title = "Deployment Initiated";
      message = "VM deployment has been initiated. This process may take a few minutes.";
      showProgress = true;
      break;
      
    case 'in_progress':
      title = "Deployment in Progress";
      message = `Creating VM "${vmName}". ${deploymentPhase || 'Please wait...'}`;
      showProgress = true;
      break;
      
    case 'completed':
      title = "Deployment Successful";
      message = `VM "${vmName}" has been successfully deployed.`;
      showViewButton = true;
      isSuccess = true;
      break;
      
    case 'failed':
      title = "Deployment Failed";
      message = "VM deployment failed. Please check the error details and try again.";
      isError = true;
      break;
      
    case 'partial':
      title = "Deployment Partial Success";
      message = `VM "${vmName}" was created but some configurations couldn't be applied.`;
      showViewButton = true;
      isWarning = true;
      break;
      
    default:
      title = "Deployment Status";
      message = "Checking deployment status...";
      showProgress = true;
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content vm-deployment-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          {isSuccess && <CheckCircle size={20} className="text-green-500" />}
          {isWarning && <AlertTriangle size={20} className="text-yellow-500" />}
          {isError && <XCircle size={20} className="text-red-500" />}
          {!isSuccess && !isWarning && !isError && <Info size={20} className="text-blue-500" />}
          <p>{title}</p>
        </div>
        
        <p className="mt-2">{message}</p>
        
        {showProgress && (
          <div className="w-full bg-gray-700 rounded-full h-2 mt-3">
            <div 
              className="bg-[#e0eacc] h-2 rounded-full" 
              style={{ width: `${progress || 0}%` }}
            ></div>
          </div>
        )}
        
        <div className="notification-actions mt-4">
          <button className="notification-button secondary" onClick={onClose}>
            Close
          </button>
          
          {showViewButton && (
            <button className="notification-button primary" onClick={() => onViewDetails(vmId)}>
              View VM Details
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const VolumeOperationNotification = ({ isVisible, operation, volumeName, size, onClose }) => {
  if (!isVisible) return null;
  
  let title = "";
  let details = [];
  
  switch (operation) {
    case 'created':
      title = "Volume Created Successfully";
      details = [
        { label: "Name", value: volumeName },
        { label: "Size", value: `${size} GB` }
      ];
      break;
    case 'attached':
      title = "Volume Attached Successfully";
      details = [
        { label: "Volume", value: volumeName }
      ];
      break;
    case 'detached':
      title = "Volume Detached Successfully";
      details = [
        { label: "Volume", value: volumeName }
      ];
      break;
    default:
      title = "Operation Completed";
      if (volumeName) details.push({ label: "Volume", value: volumeName });
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content volume-operation-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <CheckCircle size={20} className="text-green-500" />
          <p>{title}</p>
        </div>
        <div className="volume-details">
          {details.map((detail, index) => (
            <p key={index}><strong>{detail.label}:</strong> {detail.value}</p>
          ))}
        </div>
        
        <div className="notification-actions">
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const SnapshotOperationNotification = ({ isVisible, operation, snapshotName, vmName, onClose }) => {
  if (!isVisible) return null;
  
  let title = "";
  let details = [];
  
  switch (operation) {
    case 'created':
      title = "Snapshot Created Successfully";
      details = [
        { label: "Name", value: snapshotName },
        { label: "VM", value: vmName }
      ];
      break;
    case 'deleted':
      title = "Snapshot Deleted Successfully";
      details = [
        { label: "Snapshot", value: snapshotName }
      ];
      break;
    case 'restored':
      title = "Snapshot Restored Successfully";
      details = [
        { label: "Snapshot", value: snapshotName },
        { label: "New VM", value: vmName }
      ];
      break;
    default:
      title = "Operation Completed";
      if (snapshotName) details.push({ label: "Snapshot", value: snapshotName });
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content snapshot-operation-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <CheckCircle size={20} className="text-green-500" />
          <p>{title}</p>
        </div>
        <div className="snapshot-details">
          {details.map((detail, index) => (
            <p key={index}><strong>{detail.label}:</strong> {detail.value}</p>
          ))}
        </div>
        
        <div className="notification-actions">
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const SSHKeyOperationNotification = ({ isVisible, operation, keyName, fingerprint, onClose, onDownload }) => {
  if (!isVisible) return null;
  
  let title = "";
  let details = [];
  let showDownloadButton = false;
  
  switch (operation) {
    case 'created':
      title = "SSH Key Created Successfully";
      details = [
        { label: "Name", value: keyName },
        { label: "Fingerprint", value: fingerprint }
      ];
      showDownloadButton = true;
      break;
    case 'imported':
      title = "SSH Key Imported Successfully";
      details = [
        { label: "Name", value: keyName },
        { label: "Fingerprint", value: fingerprint }
      ];
      break;
    case 'deleted':
      title = "SSH Key Deleted Successfully";
      details = [
        { label: "Name", value: keyName }
      ];
      break;
    default:
      title = "Operation Completed";
      if (keyName) details.push({ label: "Key", value: keyName });
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content ssh-key-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <CheckCircle size={20} className="text-green-500" />
          <p>{title}</p>
        </div>
        <div className="ssh-key-details">
          {details.map((detail, index) => (
            <p key={index}><strong>{detail.label}:</strong> {detail.value}</p>
          ))}
          {operation === 'created' && (
            <p className="text-yellow-400 mt-2 text-sm">
              <AlertTriangle size={16} className="inline mr-1" />
              Remember to safely store your private key. You won't be able to download it again.
            </p>
          )}
        </div>
        
        <div className="notification-actions">
          {showDownloadButton && onDownload && (
            <button className="notification-button secondary mr-2" onClick={onDownload}>
              Download Private Key
            </button>
          )}
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProfileOperationNotification = ({ isVisible, operation, details, onClose }) => {
  if (!isVisible) return null;
  
  let title = "";
  let message = "";
  
  switch (operation) {
    case 'profile_updated':
      title = "Profile Updated Successfully";
      message = "Your profile information has been updated.";
      break;
    case 'password_changed':
      title = "Password Changed Successfully";
      message = "Your password has been changed. You'll use your new password the next time you sign in.";
      break;
    case 'email_verified':
      title = "Email Verified Successfully";
      message = "Your email address has been verified.";
      break;
    case 'two_factor_enabled':
      title = "Two-Factor Authentication Enabled";
      message = "Two-factor authentication has been enabled for your account.";
      break;
    case 'two_factor_disabled':
      title = "Two-Factor Authentication Disabled";
      message = "Two-factor authentication has been disabled for your account.";
      break;
    case 'api_key_generated':
      title = "API Key Generated";
      message = "Your new API key has been generated. Make sure to save it in a secure location.";
      break;
    default:
      title = "Operation Completed";
      message = "Your request has been processed successfully.";
  }
  
  return (
    <div className="notification-container">
      <div className="notification-content profile-operation-notification-content">
        <div className="notification-progress-bar"></div>
        <div className="notification-header">
          <CheckCircle size={20} className="text-green-500" />
          <p>{title}</p>
        </div>
        <div className="profile-operation-details">
          <p>{message}</p>
          {details && Object.entries(details).map(([key, value], index) => (
            <p key={index}><strong>{key}:</strong> {value}</p>
          ))}
        </div>
        
        <div className="notification-actions">
          <button className="notification-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationProvider;